// panels/AlarmView.js —— 故障告警（三类型复用，基准：光储充 p12–p13 截图）
// 两 tab：今日告警（发生中或今日结束）／历史告警（已结束且早于今日，可按时间查询）；
// 筛选栏：设备类型 / 告警等级 / 状态 + 查询 / 重置；
// 「原始告警标题」列头旁翻译图标 →「AI翻译为」下拉（不记住所选语言）。
import React, { useState } from 'react';
import { Table, Tag, Select, Button, Dropdown, DatePicker, Space } from 'antd';
import ScrollTabs from '../../../components/ScrollTabs';
import { TranslationOutlined } from '@ant-design/icons';
import { getFaultAlarmList, ALARM_DEVICE_TYPES } from '../../../config/stationData';
import { t } from '../consoles/ems/langContext';

const LEVEL_COLOR = { A: 'red', B: 'orange', C: 'blue', 未分级: 'default' };
const LANG_LIST = ['简体中文', 'English', 'Italiano', 'Deutsch', '日本語', 'Español'];

// 演示译文（真实环境由后端在线调取 AI 接口）；命中返回目标语言真实译文，未命中回退原文（避免占位副作用）
const TRANSLATIONS = {
  English: {
    '电芯温度过高': 'cell temperature too high',
    '直流侧绝缘阻抗低': 'dc insulation resistance low',
    '单体压差一级告警': 'cell voltage deviation level 1 alarm',
    '空调通信状态告警': 'hvac communication abnormal',
    'SOC 越限告警': 'soc limit exceeded alarm',
    '光伏限功率预警': 'pv power limit warning',
    '液冷出水温度告警': 'liquid cooling outlet temperature alarm',
    '用户电表通讯告警': 'user meter communication alarm',
    '三级BMS通讯异常': 'bms level 3 communication failure',
    '电池簇温差预警': 'battery cluster temperature difference warning',
  },
  Italiano: {
    '电芯温度过高': 'Temperatura cella troppo alta',
    '直流侧绝缘阻抗低': 'Resistenza di isolamento DC bassa',
    '单体压差一级告警': 'Allarme scostamento tensione cella livello 1',
    '空调通信状态告警': 'Allarme comunicazione climatizzatore',
    'SOC 越限告警': 'Allarme SOC sotto soglia',
    '光伏限功率预警': 'Avviso limitazione potenza fotovoltaico',
    '液冷出水温度告警': 'Allarme temperatura uscita raffreddamento liquido',
    '用户电表通讯告警': 'Allarme comunicazione contatore utente',
    '三级BMS通讯异常': 'Anomalia comunicazione BMS livello 3',
    '电池簇温差预警': 'Avviso scostamento temperatura batteria',
  },
  Deutsch: {
    '电芯温度过高': 'Zelltemperatur zu hoch',
    '直流侧绝缘阻抗低': 'DC-Isolationswiderstand niedrig',
    '单体压差一级告警': 'Alarm Zellspannungsabweichung Stufe 1',
    '空调通信状态告警': 'Alarm Klimageräte-Kommunikation',
    'SOC 越限告警': 'Alarm SOC unter Grenzwert',
    '光伏限功率预警': 'Warnung PV-Leistungsbegrenzung',
    '液冷出水温度告警': 'Alarm Temperatur Flüssigkeitskühlung Ausgang',
    '用户电表通讯告警': 'Alarm Benutzerzähler-Kommunikation',
    '三级BMS通讯异常': 'Störung BMS-Kommunikation Stufe 3',
    '电池簇温差预警': 'Warnung Batterietemperatur-Abweichung',
  },
  日本語: {
    '电芯温度过高': 'セル温度が高すぎます',
    '直流侧绝缘阻抗低': '直流絶縁抵抗が低い',
    '单体压差一级告警': 'セル電圧差レベル1アラーム',
    '空调通信状态告警': '空調通信アラーム',
    'SOC 越限告警': 'SOC下限アラーム',
    '光伏限功率预警': 'PV出力制限警告',
    '液冷出水温度告警': '液冷出口温度アラーム',
    '用户电表通讯告警': 'ユーザー電表通信アラーム',
    '三级BMS通讯异常': 'レベル3BMS通信異常',
    '电池簇温差预警': '電池クラスター温度差警告',
  },
  Español: {
    '电芯温度过高': 'Temperatura de celda demasiado alta',
    '直流侧绝缘阻抗低': 'Resistencia de aislamiento DC baja',
    '单体压差一级告警': 'Alarma desviación de voltaje de celda nivel 1',
    '空调通信状态告警': 'Alarma comunicación climatizador',
    'SOC 越限告警': 'Alarma SOC por debajo del límite',
    '光伏限功率预警': 'Advertencia de limitación de potencia FV',
    '液冷出水温度告警': 'Alarma temperatura salida refrigeración líquida',
    '用户电表通讯告警': 'Alarma comunicación medidor de usuario',
    '三级BMS通讯异常': 'Anomalía comunicación BMS nivel 3',
    '电池簇温差预警': 'Advertencia desviación temperatura batería',
  },
};
const translate = (origin, lang) => {
  if (!lang || lang === '简体中文' || lang === '显示原语言') return origin;
  const dict = TRANSLATIONS[lang];
  return (dict && dict[origin]) || origin;
};

const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const AlarmView = ({ lang = 'zh' }) => {
  const all = getFaultAlarmList();
  const todayStr = localToday();
  const todayList = all.filter((r) => r.status === '发生中' || (r.end && r.end !== '-' && r.end.slice(0, 10) === todayStr));
  const historyList = all.filter((r) => r.status === '已结束' && r.end && r.end.slice(0, 10) < todayStr);

  const [tab, setTab] = useState('today');
  const [aiLang, setAiLang] = useState(null); // null=原文（AI 翻译目标语言；外部 lang 为中/英界面切换）
  const [dev, setDev] = useState('全部');
  const [lvl, setLvl] = useState('全部');
  const [state, setState] = useState('全部');
  const [f, setF] = useState({ dev: '全部', lvl: '全部', state: '全部' });

  const applyF = (rows) => rows.filter((r) =>
    (f.dev === '全部' || r.deviceType === f.dev) &&
    (f.lvl === '全部' || r.level === f.lvl) &&
    (f.state === '全部' || r.status === f.state)
  );

  const reset = () => { setDev('全部'); setLvl('全部'); setState('全部'); setF({ dev: '全部', lvl: '全部', state: '全部' }); };
  const doQuery = () => setF({ dev, lvl, state });

  const lvlOptions = ['全部'].concat([...new Set(all.map((r) => r.level))]);
  const stateOptions = ['全部'].concat([...new Set(all.map((r) => r.status))]);
  const rows = applyF(tab === 'today' ? todayList : historyList);

  const langItems = [
    { type: 'group', label: t(lang, 'AI翻译为：', 'AI Translate to:') },
    ...LANG_LIST.map((l) => ({ key: l, label: l, disabled: aiLang === l })),
    { type: 'divider' },
    { key: '显示原语言', label: <span style={{ color: '#52c41a' }}>{t(lang, '显示原语言', 'Show Original')}</span>, disabled: !aiLang },
  ];

  const columns = [
    { title: t(lang, '设备名称', 'Device Name'), dataIndex: 'device', width: 200 },
    { title: t(lang, '告警等级', 'Alert Level'), dataIndex: 'level', width: 120, render: (v) => <Tag color={LEVEL_COLOR[v] || 'default'}>{t(lang, `${v} 级`, v === '未分级' ? 'Unrated' : v)}</Tag> },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {t(lang, '原始告警标题', 'Original Alarm Title')}
          <Dropdown menu={{ items: langItems, selectable: false, onClick: ({ key }) => setAiLang(key === '显示原语言' ? null : key) }} trigger={['click']}>
            <Button type="text" size="small" style={{ height: 22, minWidth: 26, padding: 0, color: '#00d4ff' }} icon={<TranslationOutlined />} />
          </Dropdown>
        </span>
      ),
      dataIndex: 'origin',
      render: (v) => translate(v, lang === 'en' ? 'English' : aiLang),
    },
    { title: t(lang, '告警发生时间', 'Alarm Time'), dataIndex: 'time', width: 180 },
    { title: t(lang, '告警结束时间', 'End Time'), dataIndex: 'end', width: 180, render: (v) => v || '-' },
    { title: t(lang, '状态', 'Status'), dataIndex: 'status', width: 100, render: (v) => <Tag color={v === '发生中' ? 'red' : 'default'}>{t(lang, v, v === '发生中' ? 'Active' : 'Ended')}</Tag> },
  ];

  const filterBar = (
    <Space size={10} wrap style={{ marginBottom: 12 }}>
      <span style={{ color: '#cfe3ff' }}>{t(lang, '设备类型', 'Device Type')}</span>
      <Select size="small" value={dev} style={{ width: 120 }} options={['全部'].concat(ALARM_DEVICE_TYPES).map((d) => ({ value: d, label: d === '全部' ? t(lang, '全部', 'All') : t(lang, d) }))} onChange={setDev} />
      <span style={{ color: '#cfe3ff' }}>{t(lang, '告警等级', 'Alert Level')}</span>
      <Select size="small" value={lvl} style={{ width: 100 }} options={lvlOptions.map((d) => ({ value: d, label: d === '全部' ? t(lang, '全部', 'All') : t(lang, d) }))} onChange={setLvl} />
      <span style={{ color: '#cfe3ff' }}>{t(lang, '状态', 'Status')}</span>
      <Select size="small" value={state} style={{ width: 100 }} options={stateOptions.map((d) => ({ value: d, label: d === '全部' ? t(lang, '全部', 'All') : t(lang, d) }))} onChange={setState} />
      <Button type="primary" size="small" onClick={doQuery}>{t(lang, '查询', 'Query')}</Button>
      <Button size="small" style={{ color: '#cfe3ff', background: 'rgba(0,0,0,0.25)', borderColor: 'rgba(0,180,216,0.3)' }} onClick={reset}>{t(lang, '重置', 'Reset')}</Button>
    </Space>
  );

  return (
    <div className="panel">
      {/* 子页切换对齐光储充「控制中心」顶部胶囊 Tabs */}
      <ScrollTabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: 'today',
            label: `${t(lang, '今日告警', 'Today Alerts')}（${todayList.length}）`,
            children: (
              <>
                {filterBar}
                <Table size="small" rowKey="key" columns={columns} dataSource={rows} pagination={{ pageSize: 8, size: 'small', showTotal: (n) => t(lang, `共 ${n} 条`, `${n} in total`) }} />
              </>
            ),
          },
          {
            key: 'history',
            label: `${t(lang, '历史告警', 'History Alerts')}（${historyList.length}）`,
            children: (
              <>
                {filterBar}
                <Space size={8} style={{ marginBottom: 12 }}>
                  <span style={{ color: '#cfe3ff' }}>{t(lang, '时间查询', 'Time Query')}</span>
                  <DatePicker.RangePicker size="small" showTime style={{ width: 380 }} placeholder={[t(lang, '开始时间', 'Start Time'), t(lang, '结束时间', 'End Time')]} />
                </Space>
                <Table size="small" rowKey="key" columns={columns} dataSource={rows} pagination={{ pageSize: 8, size: 'small', showTotal: (n) => t(lang, `共 ${n} 条`, `${n} in total`) }} />
              </>
            ),
          },
        ]}
      />
      <div className="peak-note">
        {t(lang, '原文标题旁的翻译按钮：选择目标语言进行 AI 翻译；因技术限制不会记住选择，离开本页面再次进入需重新选择语言（在线调用 AI 接口，性能受网速影响）。', 'Translate button beside the original title: choose a target language for AI translation; due to technical limits the choice is not remembered, so reselect the language after leaving and re-entering this page (online AI API, performance depends on network speed).')}
      </div>
    </div>
  );
};

export default AlarmView;